import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const LOG = '[growi-plugin-pdf-export]';
const BUTTON_ID = 'growi-plugin-pdf-export-button';
const STYLE_ID = 'growi-plugin-pdf-export-style';
const CLONE_MARKER_ATTR = 'data-growi-plugin-pdf-export-target';

// GROWI のページ本文を含む候補セレクタ。バージョン差異を吸収するため複数試行する。
const PAGE_CONTENT_SELECTORS = [
  '.main .revision-body',
  '.main .page-content',
  '.main .wiki',
  '#revision-body',
  '#page-content',
  '[data-testid="page-view"]',
  'main .wiki',
  'main',
];

let bodyObserver: MutationObserver | null = null;
let active = false;

const findPageContent = (): HTMLElement | null => {
  for (const selector of PAGE_CONTENT_SELECTORS) {
    const el = document.querySelector<HTMLElement>(selector);
    if (el != null) return el;
  }
  return null;
};

const isTransparent = (c: string): boolean =>
  c === '' || c === 'transparent' || c === 'rgba(0, 0, 0, 0)';

// 対象要素から親方向にたどって、最初に出てくる不透明な背景色を返す。
const resolveEffectiveBackground = (el: HTMLElement): string => {
  let cur: HTMLElement | null = el;
  while (cur != null) {
    const bg = getComputedStyle(cur).backgroundColor;
    if (!isTransparent(bg)) return bg;
    cur = cur.parentElement;
  }
  const bodyBg = getComputedStyle(document.body).backgroundColor;
  return isTransparent(bodyBg) ? '#ffffff' : bodyBg;
};

const parseRgb = (color: string): { r: number; g: number; b: number } => {
  const m = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (m != null) {
    return { r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) };
  }
  // 短縮の hex などのフォールバック
  if (color.startsWith('#') && (color.length === 7 || color.length === 4)) {
    if (color.length === 4) {
      return {
        r: parseInt(color[1] + color[1], 16),
        g: parseInt(color[2] + color[2], 16),
        b: parseInt(color[3] + color[3], 16),
      };
    }
    return {
      r: parseInt(color.slice(1, 3), 16),
      g: parseInt(color.slice(3, 5), 16),
      b: parseInt(color.slice(5, 7), 16),
    };
  }
  return { r: 255, g: 255, b: 255 };
};

const buildFileName = (): string => {
  const title = document.title?.trim() || 'growi-page';
  const safe = title.replace(/[\\/:*?"<>|]+/g, '_').slice(0, 120);
  const stamp = new Date().toISOString().slice(0, 10);
  return `${safe}_${stamp}.pdf`;
};

const setBusy = (button: HTMLButtonElement, busy: boolean): void => {
  button.disabled = busy;
  button.textContent = busy ? 'Exporting…' : 'Export PDF';
  button.style.opacity = busy ? '0.6' : '1';
  button.style.cursor = busy ? 'progress' : 'pointer';
};

const exportPdf = async (button: HTMLButtonElement): Promise<void> => {
  const target = findPageContent();
  if (target == null) {
    console.warn(`${LOG} page content not found. tried:`, PAGE_CONTENT_SELECTORS);
    alert('[PDF Export] ページ本文が見つかりませんでした。');
    return;
  }
  const bg = resolveEffectiveBackground(target);
  const fg = getComputedStyle(target).color || getComputedStyle(document.body).color;
  const rgb = parseRgb(bg);
  console.warn(`${LOG} v3 exporting target:`, target, 'bg:', bg, 'fg:', fg);

  target.setAttribute(CLONE_MARKER_ATTR, '1');
  setBusy(button, true);
  try {
    // 1. キャプチャ
    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: bg,
      onclone: (clonedDoc) => {
        const root = clonedDoc.documentElement;
        const body = clonedDoc.body;
        if (root != null) {
          root.style.background = bg;
          root.style.color = fg;
        }
        if (body != null) {
          body.style.background = bg;
          body.style.color = fg;
        }
        const clonedTarget = clonedDoc.querySelector<HTMLElement>(
          `[${CLONE_MARKER_ATTR}]`,
        );
        if (clonedTarget != null) {
          clonedTarget.style.background = bg;
          clonedTarget.style.color = fg;
          clonedTarget.style.padding = '24px';
          clonedTarget.style.boxSizing = 'border-box';
        }
      },
    });

    // 2. PDF を構築: A4 幅 × 必要な縦の長さで「1 ページ完結」させる。
    //    こうすることでページ境界による見切れがなくなる。
    const pageWidthMm = 210; // A4 幅
    const pageHeightMm = (canvas.height / canvas.width) * pageWidthMm;

    // PDF 仕様 (および多くのビューア) の安全側上限の目安: 5080mm (=200in)。
    // それを超える場合のみ複数ページに分割する。
    const SINGLE_PAGE_MAX_MM = 5080;

    const pdf = new jsPDF({
      unit: 'mm',
      format: [pageWidthMm, Math.min(pageHeightMm, SINGLE_PAGE_MAX_MM)],
      // 縦長/横長は実寸から自動。orientation は明示しない (jsPDF が
      // 値を勝手に入れ替えて縦横を逆転させるのを避けるため)
    });

    console.warn(
      `${LOG} canvas=${canvas.width}x${canvas.height} pageMm=${pageWidthMm}x${pageHeightMm.toFixed(1)}`,
    );

    if (pageHeightMm <= SINGLE_PAGE_MAX_MM) {
      // 1 ページに丸ごと収める
      pdf.setFillColor(rgb.r, rgb.g, rgb.b);
      pdf.rect(0, 0, pageWidthMm, pageHeightMm, 'F');
      pdf.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        0,
        0,
        pageWidthMm,
        pageHeightMm,
      );
      console.warn(`${LOG} saved as single page (${pageHeightMm.toFixed(1)}mm tall)`);
    } else {
      // 上限を超える場合のみ分割 (各ページの寸法もカスタムで隙間を作らない)
      const pxPerMm = canvas.width / pageWidthMm;
      const sliceMaxPx = Math.floor(SINGLE_PAGE_MAX_MM * pxPerMm);
      const numPages = Math.ceil(canvas.height / sliceMaxPx);
      console.warn(`${LOG} content exceeds ${SINGLE_PAGE_MAX_MM}mm; splitting into ${numPages} page(s)`);

      // 1 ページ目は jsPDF 初期化時に作成済み。i=0 ではそのまま使う。
      for (let i = 0; i < numPages; i++) {
        const sliceHeightPx = Math.min(sliceMaxPx, canvas.height - i * sliceMaxPx);
        const sliceHeightMm = sliceHeightPx / pxPerMm;
        if (i > 0) pdf.addPage([pageWidthMm, sliceHeightMm], 'portrait');

        pdf.setFillColor(rgb.r, rgb.g, rgb.b);
        pdf.rect(0, 0, pageWidthMm, sliceHeightMm, 'F');

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext('2d');
        if (ctx == null) throw new Error('2d context unavailable');
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(
          canvas,
          0,
          i * sliceMaxPx,
          canvas.width,
          sliceHeightPx,
          0,
          0,
          canvas.width,
          sliceHeightPx,
        );

        pdf.addImage(
          sliceCanvas.toDataURL('image/png'),
          'PNG',
          0,
          0,
          pageWidthMm,
          sliceHeightMm,
        );
      }
    }

    pdf.save(buildFileName());
  } catch (err) {
    console.error(`${LOG} export failed:`, err);
    alert('[PDF Export] エクスポートに失敗しました。コンソールを確認してください。');
  } finally {
    target.removeAttribute(CLONE_MARKER_ATTR);
    setBusy(button, false);
  }
};

const injectStyles = (): void => {
  if (document.getElementById(STYLE_ID) != null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    #${BUTTON_ID} {
      position: fixed !important;
      right: 20px !important;
      bottom: 20px !important;
      z-index: 2147483647 !important;
      padding: 10px 16px !important;
      border: none !important;
      border-radius: 999px !important;
      background: #3a6df0 !important;
      color: #fff !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18) !important;
      cursor: pointer !important;
      display: inline-block !important;
      visibility: visible !important;
      opacity: 1 !important;
      pointer-events: auto !important;
      transition: transform 0.1s ease, box-shadow 0.1s ease;
    }
    #${BUTTON_ID}:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.22) !important;
    }
    #${BUTTON_ID}:disabled {
      cursor: progress !important;
    }
  `;
  (document.head ?? document.documentElement).appendChild(style);
};

const createButton = (): HTMLButtonElement => {
  const button = document.createElement('button');
  button.id = BUTTON_ID;
  button.type = 'button';
  button.textContent = 'Export PDF';
  button.setAttribute('aria-label', 'Export the current page as PDF');
  button.addEventListener('click', () => {
    void exportPdf(button);
  });
  return button;
};

const ensureButton = (): void => {
  if (!active) return;
  if (document.getElementById(BUTTON_ID) != null) return;
  if (document.body == null) return;
  injectStyles();
  document.body.appendChild(createButton());
  console.info(`${LOG} button mounted`);
};

const startBodyObserver = (): void => {
  if (bodyObserver != null) return;
  if (document.body == null) return;
  bodyObserver = new MutationObserver(() => {
    ensureButton();
  });
  bodyObserver.observe(document.body, { childList: true });
};

const waitForBody = (cb: () => void): void => {
  if (document.body != null) {
    cb();
    return;
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cb, { once: true });
    return;
  }
  const id = window.setInterval(() => {
    if (document.body != null) {
      window.clearInterval(id);
      cb();
    }
  }, 50);
};

export const mountPdfExportButton = (): void => {
  active = true;
  console.info(`${LOG} activate (readyState=${document.readyState})`);
  waitForBody(() => {
    ensureButton();
    startBodyObserver();
  });
};

export const unmountPdfExportButton = (): void => {
  active = false;
  bodyObserver?.disconnect();
  bodyObserver = null;
  document.getElementById(BUTTON_ID)?.remove();
  document.getElementById(STYLE_ID)?.remove();
  console.info(`${LOG} deactivate`);
};
